import {
  EMediaSequenceMutationTemplateType,
  MediaSequenceCreateNewRequest,
  MediaSequenceMutationTemplateResponse,
} from 'api/core';
import { useSearchMediaSequenceMutationTemplates } from 'api/useMediaSequenceMutationTemplatesApi';
import { ControlledModal } from 'components/ControlledModal';
import { LabelWithHelperText } from 'components/Form/LabelWithHelperText';
import { AnimatedIcon } from 'components/Icon/AnimatedIcon';
import { EntitySelectSingleAsync } from 'components/Select/EntitySelectSingleAsync';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

interface MediaSequenceCreateFormTemplateStepProps {
  inDialog?: boolean;
  disabled?: boolean;
}

export const MediaSequenceCreateFormTemplateStep = ({
  inDialog,
  disabled,
}: MediaSequenceCreateFormTemplateStepProps) => {
  const { setValue, control } = useFormContext<MediaSequenceCreateNewRequest>();
  const mutationTemplateIds = useWatch({
    control,
    name: 'mutationTemplateIds',
  });

  const [previewTarget, setPreviewTarget] =
    useState<MediaSequenceMutationTemplateResponse | null>(null);

  const [selectedIntroTemplate, setSelectedIntroTemplate] =
    useState<MediaSequenceMutationTemplateResponse | null>(null);

  const [selectedOutroTemplate, setSelectedOutroTemplate] =
    useState<MediaSequenceMutationTemplateResponse | null>(null);

  const { data: allMutationTemplates = [] } =
    useSearchMediaSequenceMutationTemplates({
      types: [
        EMediaSequenceMutationTemplateType.Intro,
        EMediaSequenceMutationTemplateType.Outro,
        EMediaSequenceMutationTemplateType.Miscellaneous,
      ],
    });

  const selectedTemplates = allMutationTemplates.filter((t) =>
    mutationTemplateIds?.includes(t.id)
  );

  const upsertTemplate = (
    template: MediaSequenceMutationTemplateResponse,
    allowMultipleOfType = false
  ) => {
    let updated = selectedTemplates;

    if (!allowMultipleOfType) {
      updated = updated.filter((t) => t.type !== template.type);
    } else {
      updated = updated.filter((t) => t.id !== template.id);
    }

    updated = [...updated, template];
    setValue(
      'mutationTemplateIds',
      updated.map((t) => t.id),
      { shouldValidate: true }
    );
  };

  const removeTemplate = (template: MediaSequenceMutationTemplateResponse) => {
    const updated = selectedTemplates.filter((t) => t.id !== template.id);
    setValue(
      'mutationTemplateIds',
      updated.map((t) => t.id),
      { shouldValidate: true }
    );
  };

  const onIntroSelected = (
    template: MediaSequenceMutationTemplateResponse | null
  ) => {
    setSelectedIntroTemplate(template);
    if (!template) {
      const updated = selectedTemplates.filter(
        (t) => t.type !== EMediaSequenceMutationTemplateType.Intro
      );
      setValue(
        'mutationTemplateIds',
        updated.map((t) => t.id),
        { shouldValidate: true }
      );
    } else {
      upsertTemplate(template, false);
    }
  };

  const onOutroSelected = (
    template: MediaSequenceMutationTemplateResponse | null
  ) => {
    setSelectedOutroTemplate(template);
    if (!template) {
      const updated = selectedTemplates.filter(
        (t) => t.type !== EMediaSequenceMutationTemplateType.Outro
      );
      setValue(
        'mutationTemplateIds',
        updated.map((t) => t.id),
        { shouldValidate: true }
      );
    } else {
      upsertTemplate(template, false);
    }
  };

  const onMiscSelected = (template: MediaSequenceMutationTemplateResponse) => {
    const isSelected = mutationTemplateIds?.includes(template.id);
    if (isSelected) {
      removeTemplate(template);
    } else {
      upsertTemplate(template, true);
    }
  };

  const isMiscSelected = (template: MediaSequenceMutationTemplateResponse) => {
    return mutationTemplateIds?.includes(template.id);
  };

  return (
    <>
      <div className="form-control">
        <LabelWithHelperText
          label={<p>Vælg intro</p>}
          helperText="Vælg en intro til videoen."
          inDialog={inDialog}
        />
        <EntitySelectSingleAsync<
          MediaSequenceMutationTemplateResponse | null,
          { searchTerm?: string; types?: EMediaSequenceMutationTemplateType[] }
        >
          useSearchFunction={useSearchMediaSequenceMutationTemplates}
          searchParams={{ types: [EMediaSequenceMutationTemplateType.Intro] }}
          renderSuggestion={(mutationTemplate) =>
            mutationTemplate ? (
              <div className="flex items-center justify-start space-x-2">
                <p>
                  {mutationTemplate.name} ({mutationTemplate.description})
                </p>
              </div>
            ) : null
          }
          onSuggestionSelected={onIntroSelected}
          initialValue={
            selectedTemplates.find(
              (t) => t.type === EMediaSequenceMutationTemplateType.Intro
            ) ?? null
          }
          inDialog={inDialog}
          disabled={disabled}
        />
        {selectedIntroTemplate?.previewUrl ? (
          <div
            className="flex items-center space-x-2 cursor-pointer mt-2"
            onClick={() => setPreviewTarget(selectedIntroTemplate)}
          >
            <AnimatedIcon icon="open-icon" className="h-8 md:h-6 w-8 md:w-6" />
            <span className="label-text">
              Forhåndvisning af {selectedIntroTemplate.name}
            </span>
          </div>
        ) : null}
      </div>

      <div className="form-control">
        <LabelWithHelperText
          label={<p>Vælg outro</p>}
          helperText="Vælg en outro til videoen."
          inDialog={inDialog}
        />
        <EntitySelectSingleAsync<
          MediaSequenceMutationTemplateResponse | null,
          { searchTerm?: string; types?: EMediaSequenceMutationTemplateType[] }
        >
          useSearchFunction={useSearchMediaSequenceMutationTemplates}
          searchParams={{ types: [EMediaSequenceMutationTemplateType.Outro] }}
          renderSuggestion={(mutationTemplate) =>
            mutationTemplate ? (
              <div className="flex items-center justify-start space-x-2">
                <p>
                  {mutationTemplate.name} ({mutationTemplate.description})
                </p>
              </div>
            ) : null
          }
          onSuggestionSelected={onOutroSelected}
          initialValue={
            selectedTemplates.find(
              (t) => t.type === EMediaSequenceMutationTemplateType.Outro
            ) ?? null
          }
          inDialog={inDialog}
          disabled={disabled}
        />
        {selectedOutroTemplate?.previewUrl ? (
          <div
            className="flex items-center space-x-2 cursor-pointer mt-2"
            onClick={() => setPreviewTarget(selectedOutroTemplate)}
          >
            <AnimatedIcon icon="open-icon" className="h-8 md:h-6 w-8 md:w-6" />
            <span className="label-text">
              Forhåndvisning af {selectedOutroTemplate.name}
            </span>
          </div>
        ) : null}
      </div>

      {allMutationTemplates.some(
        (t) => t.type === EMediaSequenceMutationTemplateType.Miscellaneous
      ) && (
        <div className="pt-2">
          <LabelWithHelperText
            label={<p className="font-semibold">Vælg ekstra skabeloner</p>}
            helperText="Vælg en ekstra skabeloner til videoen."
            inDialog={inDialog}
          />
          <div className="form-control">
            {allMutationTemplates
              .filter(
                (t) =>
                  t.type === EMediaSequenceMutationTemplateType.Miscellaneous
              )
              .map((template) => (
                <div
                  className="flex items-center justify-between"
                  key={template.id}
                >
                  <div className="flex items-center space-x-2">
                    {template.previewUrl ? (
                      <div
                        className="cursor-pointer"
                        onClick={() => setPreviewTarget(template)}
                      >
                        <AnimatedIcon
                          icon="open-icon"
                          className="h-8 md:h-6 w-8 md:w-6"
                        />
                      </div>
                    ) : null}
                    <span className="label-text">{template.name}</span>
                  </div>
                  <label className="label cursor-pointer">
                    <input
                      type="checkbox"
                      className="toggle toggle-primary"
                      checked={isMiscSelected(template)}
                      onChange={() => onMiscSelected(template)}
                      disabled={disabled}
                    />
                  </label>
                </div>
              ))}
          </div>
        </div>
      )}

      {previewTarget && previewTarget.previewUrl ? (
        <ControlledModal
          showModal={() => setPreviewTarget(null)}
          isOpen={previewTarget !== null}
          title={'Skabelon forhåndvisning'}
          closeOnOutsideClick
          hideActionBar
        >
          <div className="flex flex-col items-center space-y-4">
            <img
              src={previewTarget.previewUrl}
              alt="Preview"
              className="w-full h-full"
            />
            <p className="text-center">
              Dette er en forhåndsvisning af skabelonen{' '}
              <span className="font-bold">{previewTarget.name}</span>. Det
              endelige resultat kan variere afhængigt af, hvilken butik
              skabelonen bruges i, samt eventuelle ændringer du foretager på
              skabelonen.
            </p>
          </div>
        </ControlledModal>
      ) : null}
    </>
  );
};
